import { c as _c } from "react/compiler-runtime";
import { getAllChangelogPosts } from '@onefootprint/changelog';
import { getPrivateAccessRequestsOptions } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { useQuery } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { useRouter } from 'next/router';
import { useQueryState } from 'nuqs';
import { useEffect } from 'react';
import useChatWidgetLayout from '@/components/agent-chat/hooks/use-chat-widget-layout';
import { DEFAULT_PUBLIC_ROUTE } from '@/config/constants';
import useAssumeAuthRole from '@/hooks/use-assume-auth-role';
import useAuthRoles from '@/hooks/use-auth-roles';
import useSession from '@/hooks/use-session';
import ReviewsDropdown from '../reviews-dropdown/reviews-dropdown';
import DevelopersDropdown from './components/developers-dropdown';
import NavFooter from './components/nav-footer';
import NavHeader from './components/nav-header';
import NavLink from './components/nav-link';
import ReportingDropdown from './components/reporting-dropdown';
import SettingsDropdown from './components/settings-dropdown';
import WhatsNewBanner from './components/whats-new-banner';
import useChangelogPosts from './hooks/use-changelog-posts';
import useRoutes from './hooks/use-routes/use-routes';
import moveTenantToFront from './utils/move-tenant-to-front';
type SideNavProps = {
  isCollapsed: boolean;
  onSideNavCollapse?: (isCollapsed: boolean) => void;
};
const posts = getAllChangelogPosts();
const Nav = t0 => {
  const $ = _c(80);
  const {
    onSideNavCollapse,
    isCollapsed
  } = t0;
  const router = useRouter();
  const {
    hasUnseenPosts,
    markSeen
  } = useChangelogPosts(posts);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      history: "push"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [, setWhatsNew] = useQueryState("whats-new", t1);
  const {
    dangerouslyCastedData,
    logIn,
    data: t2
  } = useSession();
  const {
    user,
    org
  } = t2;
  const assumeRoleMutation = useAssumeAuthRole();
  const showErrorToast = useRequestErrorToast();
  const {
    isFullscreen,
    setMode
  } = useChatWidgetLayout();
  const tenantsQuery = useAuthRoles(dangerouslyCastedData.auth);
  const currTenantId = dangerouslyCastedData.org.id;
  let t3;
  if ($[1] !== tenantsQuery.data) {
    t3 = tenantsQuery.data ?? [];
    $[1] = tenantsQuery.data;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== currTenantId || $[4] !== t3) {
    t4 = moveTenantToFront(t3, currTenantId);
    $[3] = currTenantId;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const tenants = t4;
  let t5;
  if ($[6] !== currTenantId || $[7] !== dangerouslyCastedData.org.name || $[8] !== tenantsQuery.data) {
    t5 = tenantsQuery.data && !tenantsQuery.data.some(tenant => tenant.id === currTenantId) ? {
      id: currTenantId,
      name: dangerouslyCastedData.org.name
    } : undefined;
    $[6] = currTenantId;
    $[7] = dangerouslyCastedData.org.name;
    $[8] = tenantsQuery.data;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const assumedTenant = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = getPrivateAccessRequestsOptions();
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = Boolean(user?.isFirmEmployee);
  let t8;
  if ($[11] !== t7) {
    t8 = {
      ...t6,
      enabled: t7,
      staleTime: 60000
    };
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const {
    data: accessRequests
  } = useQuery(t8);
  let t9;
  bb0: {
    if (!Array.isArray(accessRequests)) {
      t9 = 0;
      break bb0;
    }
    let t10;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = new Date().toISOString();
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    const now = t10;
    let t11;
    if ($[14] !== accessRequests || $[15] !== now) {
      let t12;
      if ($[17] !== now) {
        t12 = r => r.expiresAt > now && r.respondedAt === null;
        $[17] = now;
        $[18] = t12;
      } else {
        t12 = $[18];
      }
      t11 = accessRequests.filter(t12);
      $[14] = accessRequests;
      $[15] = now;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    t9 = t11.length;
  }
  const outstandingRiskOpsRequests = t9;
  const t10 = user?.isRiskOpsEmployee;
  let t11;
  if ($[19] !== outstandingRiskOpsRequests || $[20] !== t10) {
    t11 = {
      riskOpsRequests: outstandingRiskOpsRequests,
      isRiskOpsTeamMember: t10
    };
    $[19] = outstandingRiskOpsRequests;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const routes = useRoutes(t11);
  let t12;
  if ($[22] !== routes) {
    t12 = routes.flatMap(_temp2).join(",");
    $[22] = routes;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const hrefsKey = t12;
  let t13;
  let t14;
  if ($[24] !== hrefsKey || $[25] !== router || $[26] !== user) {
    t13 = () => {
      if (!user) {
        return;
      }
      const hrefs = hrefsKey.split(",");
      const prefetchAll = () => {
        hrefs.forEach(href => {
          router.prefetch(href).catch(_temp3);
        });
      };
      const hasIdleCallback = typeof window.requestIdleCallback === "function";
      const id = hasIdleCallback ? window.requestIdleCallback(prefetchAll) : window.setTimeout(prefetchAll, 200);
      return () => {
        if (hasIdleCallback) {
          window.cancelIdleCallback(id);
        } else {
          window.clearTimeout(id);
        }
      };
    };
    t14 = [user, hrefsKey, router];
    $[24] = hrefsKey;
    $[25] = router;
    $[26] = user;
    $[27] = t13;
    $[28] = t14;
  } else {
    t13 = $[27];
    t14 = $[28];
  }
  useEffect(t13, t14);
  let t15;
  if ($[29] !== markSeen || $[30] !== setWhatsNew) {
    t15 = () => {
      markSeen();
      setWhatsNew("open");
    };
    $[29] = markSeen;
    $[30] = setWhatsNew;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const handleWhatsNewOpen = t15;
  let t16;
  if ($[32] !== assumeRoleMutation || $[33] !== dangerouslyCastedData.auth || $[34] !== logIn || $[35] !== router || $[36] !== showErrorToast) {
    t16 = tenantId => {
      if (assumeRoleMutation.isPending) {
        return;
      }
      const authToken = dangerouslyCastedData.auth;
      assumeRoleMutation.mutate({
        tenantId,
        authToken
      }, {
        async onSuccess(t17) {
          const {
            token
          } = t17;
          await logIn({
            auth: token
          });
          router.push(DEFAULT_PUBLIC_ROUTE);
        },
        onError: showErrorToast
      });
    };
    $[32] = assumeRoleMutation;
    $[33] = dangerouslyCastedData.auth;
    $[34] = logIn;
    $[35] = router;
    $[36] = showErrorToast;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const handleAssumeTenant = t16;
  const pathname = router.pathname;
  let t17;
  let t18;
  if ($[38] !== isFullscreen || $[39] !== router.events || $[40] !== setMode) {
    t17 = () => {
      const handleRouteChangeStart = () => {
        if (isFullscreen) {
          setMode("floating");
        }
      };
      router.events.on("routeChangeStart", handleRouteChangeStart);
      return () => router.events.off("routeChangeStart", handleRouteChangeStart);
    };
    t18 = [router.events, isFullscreen, setMode];
    $[38] = isFullscreen;
    $[39] = router.events;
    $[40] = setMode;
    $[41] = t17;
    $[42] = t18;
  } else {
    t17 = $[41];
    t18 = $[42];
  }
  useEffect(t17, t18);
  const t19 = isCollapsed ? "md:w-(--side-nav-width-collapsed)" : "md:w-(--side-nav-width)";
  let t20;
  if ($[43] !== t19) {
    t20 = cx("relative w-full h-full md:h-dvh flex flex-col pt-2", t19);
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== isCollapsed || $[46] !== onSideNavCollapse) {
    t21 = <NavHeader isCollapsed={isCollapsed} onSideNavCollapse={onSideNavCollapse} />;
    $[45] = isCollapsed;
    $[46] = onSideNavCollapse;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== isCollapsed || $[49] !== org?.featureGates || $[50] !== pathname || $[51] !== routes || $[52] !== user?.isFirmEmployee) {
    let t23;
    if ($[54] !== isCollapsed || $[55] !== org?.featureGates || $[56] !== pathname || $[57] !== user?.isFirmEmployee) {
      t23 = (t24, groupIdx) => {
        const {
          items
        } = t24;
        const filteredItems = items.filter(t25 => {
          const {
            employeesOnly,
            featureGate,
            isVisible
          } = t25;
          if (isVisible !== undefined) {
            return isVisible;
          }
          if (featureGate) {
            return !!user?.isFirmEmployee || !!org?.featureGates?.includes(featureGate);
          }
          return !employeesOnly || !!user?.isFirmEmployee;
        });
        return filteredItems.length === 0 ? null : <ul className="flex flex-col gap-0.5 list-none m-0 p-0" key={groupIdx}>{filteredItems.map(t26 => {
            const {
              text,
              href: href_0,
              Icon,
              activeOn
            } = t26;
            const selected = pathname.startsWith(href_0) || (activeOn?.some(prefix => pathname.startsWith(prefix)) ?? false);
            return <li key={text}>{href_0 === "/reviews" && <ReviewsDropdown href={href_0} icon={Icon} text={text} isCollapsed={isCollapsed} pathname={pathname} />}{href_0 === "/developers" && <DevelopersDropdown text={text} icon={Icon} isCollapsed={isCollapsed} pathname={pathname} />}{href_0 === "/reporting" && <ReportingDropdown text={text} icon={Icon} isCollapsed={isCollapsed} pathname={pathname} />}{href_0 === "/settings" && <SettingsDropdown href={href_0} icon={Icon} text={text} isCollapsed={isCollapsed} pathname={pathname} />}{href_0 !== "/reviews" && href_0 !== "/developers" && href_0 !== "/reporting" && href_0 !== "/settings" && <NavLink href={href_0} icon={Icon} selected={selected} text={text} isCollapsed={isCollapsed} />}</li>;
          })}</ul>;
      };
      $[54] = isCollapsed;
      $[55] = org?.featureGates;
      $[56] = pathname;
      $[57] = user?.isFirmEmployee;
      $[58] = t23;
    } else {
      t23 = $[58];
    }
    t22 = routes.map(t23);
    $[48] = isCollapsed;
    $[49] = org?.featureGates;
    $[50] = pathname;
    $[51] = routes;
    $[52] = user?.isFirmEmployee;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[59] !== t22) {
    t23 = <div className="flex flex-col flex-1 overflow-auto px-3 pb-3 pt-2"><div className="flex flex-col gap-8">{t22}</div></div>;
    $[59] = t22;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== handleWhatsNewOpen || $[62] !== hasUnseenPosts || $[63] !== isCollapsed || $[64] !== markSeen) {
    t24 = !isCollapsed && <div className="px-3 pb-2"><WhatsNewBanner open={hasUnseenPosts} onDismiss={markSeen} onWhatsNewOpen={handleWhatsNewOpen} /></div>;
    $[61] = handleWhatsNewOpen;
    $[62] = hasUnseenPosts;
    $[63] = isCollapsed;
    $[64] = markSeen;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== assumeRoleMutation.isPending || $[67] !== assumedTenant || $[68] !== currTenantId || $[69] !== handleAssumeTenant || $[70] !== isCollapsed || $[71] !== tenants || $[72] !== user) {
    t25 = <NavFooter isCollapsed={isCollapsed} currTenantId={currTenantId} assumedTenant={assumedTenant} onAssumeTenant={handleAssumeTenant} isSwitchingTenant={assumeRoleMutation.isPending} tenants={tenants} posts={posts} user={user} />;
    $[66] = assumeRoleMutation.isPending;
    $[67] = assumedTenant;
    $[68] = currTenantId;
    $[69] = handleAssumeTenant;
    $[70] = isCollapsed;
    $[71] = tenants;
    $[72] = user;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  let t26;
  if ($[74] !== t20 || $[75] !== t21 || $[76] !== t23 || $[77] !== t24 || $[78] !== t25) {
    t26 = <nav aria-label="Primary" className={t20}>{t21}{t23}{t24}{t25}</nav>;
    $[74] = t20;
    $[75] = t21;
    $[76] = t23;
    $[77] = t24;
    $[78] = t25;
    $[79] = t26;
  } else {
    t26 = $[79];
  }
  return t26;
};
export default Nav;
function _temp(i) {
  return i.href;
}
function _temp2(g) {
  return g.items.map(_temp);
}
function _temp3() {}